
// =============================================================================
// CONFIGURATION
// =============================================================================

const CUTOFF_HOUR = 15; // 3 PM
const TIMEZONE = 'America/Chicago';
const HOLIDAYS_CACHE_KEY = 'ndt_holidays_cache';
const HOLIDAYS_CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// =============================================================================
// HOLIDAY DATA MANAGEMENT
// =============================================================================

let holidaysCache = null;

/**
 * Get custom closures from window object (set by Liquid template)
 * @returns {string[]} Array of date strings in YYYY-MM-DD format
 */
function getCustomClosures() {
  if (!window.customClosures || !Array.isArray(window.customClosures)) {
    return [];
  }
  return window.customClosures.map(c => c.date).filter(Boolean);
}

// Holidays that Next Day Tees actually observes (by Nager.Date localName)
const OBSERVED_HOLIDAYS = [
  'New Year\'s Day',
  'Memorial Day',
  'Independence Day',
  'Labour Day', // Nager.Date uses British spelling
  'Thanksgiving Day',
  'Christmas Day'
];

/**
 * Get Christmas Eve and New Year's Eve dates for a given year
 * These are not federal holidays but NDT observes them
 * @param {number} year
 * @returns {string[]} Array of date strings in YYYY-MM-DD format
 */
function getExtraClosureDates(year) {
  return [
    `${year}-12-24`, // Christmas Eve
    `${year}-12-31`  // New Year's Eve
  ];
}

/**
 * Fetch US public holidays from Nager.Date API
 * @param {number} year - The year to fetch holidays for
 * @returns {Promise<string[]>} Array of date strings in YYYY-MM-DD format
 */
async function fetchHolidaysForYear(year) {
  try {
    const response = await fetch(`https://date.nager.at/api/v3/PublicHolidays/${year}/US`);
    if (!response.ok) {
      console.warn(`Failed to fetch holidays for ${year}: ${response.status}`);
      return getExtraClosureDates(year);
    }
    const holidays = await response.json();
    // Filter to only holidays NDT actually observes
    const apiHolidays = holidays
      .filter(h => OBSERVED_HOLIDAYS.includes(h.localName))
      .map(h => h.date);
    
    // Add Christmas Eve and New Year's Eve (not in API)
    return [...apiHolidays, ...getExtraClosureDates(year)];
  } catch (error) {
    console.warn('Error fetching holidays:', error);
    return getExtraClosureDates(year);
  }
}

/**
 * Load holidays from cache or fetch from API
 * @returns {Promise<string[]>} Array of holiday date strings
 */
async function loadHolidays() {
  // Return cached holidays if already loaded
  if (holidaysCache !== null) {
    return holidaysCache;
  }

  // Check localStorage cache
  const cached = localStorage.getItem(HOLIDAYS_CACHE_KEY);
  if (cached) {
    try {
      const { holidays, timestamp } = JSON.parse(cached);
      if (Date.now() - timestamp < HOLIDAYS_CACHE_TTL) {
        holidaysCache = holidays;
        return holidays;
      }
    } catch (e) {
      // Invalid cache, will refetch
    }
  }

  // Fetch holidays for current year and next year (for year boundary)
  const now = getCentralTime();
  const currentYear = now.getFullYear();
  const [currentYearHolidays, nextYearHolidays] = await Promise.all([
    fetchHolidaysForYear(currentYear),
    fetchHolidaysForYear(currentYear + 1)
  ]);

  holidaysCache = [...currentYearHolidays, ...nextYearHolidays];

  // Cache to localStorage
  try {
    localStorage.setItem(HOLIDAYS_CACHE_KEY, JSON.stringify({
      holidays: holidaysCache,
      timestamp: Date.now()
    }));
  } catch (e) {
    // localStorage might be full or disabled
  }

  return holidaysCache;
}

// =============================================================================
// DATE UTILITIES
// =============================================================================

/**
 * Get current date/time in Central Time
 * @returns {Date} Date object representing current time in Central Time
 */
function getCentralTime() {
  const now = new Date();
  const centralTimeStr = now.toLocaleString('en-US', {
    timeZone: TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });

  // Parse MM/DD/YYYY, HH:MM:SS format
  const match = centralTimeStr.match(/(\d{2})\/(\d{2})\/(\d{4}),\s*(\d{2}):(\d{2}):(\d{2})/);
  if (match) {
    return new Date(match[3], match[1] - 1, match[2], match[4], match[5], match[6]);
  }

  // Fallback to approximate conversion
  return new Date(now.toLocaleString('en-US', { timeZone: TIMEZONE }));
}

/**
 * Format a date as YYYY-MM-DD
 * @param {Date} date
 * @returns {string}
 */
function formatDateString(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Get current day of week as number (0-6, Sunday=0)
 * @returns {number}
 */
function getCurrentDayInt() {
  return getCentralTime().getDay();
}

/**
 * Check if current time is after 3pm Central Time
 * @returns {boolean}
 */
function isAfterCutoff() {
  return getCentralTime().getHours() >= CUTOFF_HOUR;
}

/**
 * Check if a date is a weekend (Saturday or Sunday)
 * @param {Date} date
 * @returns {boolean}
 */
function isWeekend(date) {
  const day = date.getDay();
  return day === 0 || day === 6;
}

/**
 * Check if a specific date is closed (weekend, holiday, or custom closure)
 * @param {Date} date
 * @param {string[]} holidays - Array of holiday dates in YYYY-MM-DD format
 * @param {string[]} customClosures - Array of custom closure dates in YYYY-MM-DD format
 * @returns {boolean}
 */
function isBusinessClosed(date, holidays, customClosures) {
  // Check weekend
  if (isWeekend(date)) {
    return true;
  }

  const dateStr = formatDateString(date);

  // Check holidays
  if (holidays.includes(dateStr)) {
    return true;
  }

  // Check custom closures
  if (customClosures.includes(dateStr)) {
    return true;
  }

  return false;
}

/**
 * Get the next business day from a starting date, skipping closed days
 * @param {Date} startDate - The date to start from
 * @param {number} businessDaysToAdd - Number of business days to add (default 1)
 * @param {string[]} holidays - Array of holiday dates
 * @param {string[]} customClosures - Array of custom closure dates
 * @returns {Date} The next open business day
 */
function getNextBusinessDay(startDate, businessDaysToAdd, holidays, customClosures) {
  const result = new Date(startDate);
  let daysAdded = 0;
  let iterations = 0;
  const maxIterations = 30; // Safety limit

  while (daysAdded < businessDaysToAdd && iterations < maxIterations) {
    result.setDate(result.getDate() + 1);
    iterations++;

    if (!isBusinessClosed(result, holidays, customClosures)) {
      daysAdded++;
    }
  }

  return result;
}

/**
 * Get the day name for a date, or 'tomorrow' if it's the next calendar day
 * @param {Date} targetDate
 * @param {Date} referenceDate
 * @returns {string}
 */
function getDayLabel(targetDate, referenceDate) {
  const refDateOnly = new Date(referenceDate.getFullYear(), referenceDate.getMonth(), referenceDate.getDate());
  const targetDateOnly = new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate());

  const diffDays = Math.round((targetDateOnly - refDateOnly) / (1000 * 60 * 60 * 24));

  if (diffDays === 1) {
    return 'tomorrow';
  }

  return DAY_NAMES[targetDate.getDay()];
}

// =============================================================================
// CUTOFF CALCULATIONS
// =============================================================================

/**
 * Calculate the next cutoff date/time and related dates
 * @param {string[]} holidays
 * @param {string[]} customClosures
 * @returns {{ orderByLabel: string, pickupDay: string, shipDay: string, cutoffDate: Date }}
 */
function calculateCutoffDates(holidays, customClosures) {
  const now = getCentralTime();
  const afterCutoff = isAfterCutoff();

  // Determine the effective "order date" - today if before cutoff, tomorrow if after
  let effectiveOrderDate = new Date(now);

  // If after cutoff, the order won't be processed until next business day
  if (afterCutoff) {
    effectiveOrderDate = getNextBusinessDay(now, 1, holidays, customClosures);
  } else {
    // Before cutoff - check if today is a business day
    if (isBusinessClosed(now, holidays, customClosures)) {
      effectiveOrderDate = getNextBusinessDay(now, 1, holidays, customClosures);
    }
  }

  // Order by label: "today" if before cutoff and today is a business day, otherwise the next business day
  let orderByLabel;
  if (!afterCutoff && !isBusinessClosed(now, holidays, customClosures)) {
    orderByLabel = 'today';
  } else {
    orderByLabel = getDayLabel(effectiveOrderDate, now);
  }

  // Pickup day: 1 business day after the effective order date
  const pickupDate = getNextBusinessDay(effectiveOrderDate, 1, holidays, customClosures);
  const pickupDay = getDayLabel(pickupDate, now);

  // Ship day: 2 business days after effective order date (production + shipping)
  const shipDate = getNextBusinessDay(effectiveOrderDate, 2, holidays, customClosures);
  const shipDay = getDayLabel(shipDate, now);

  // Calculate the cutoff datetime (3pm on the effective order date)
  const cutoffDate = new Date(effectiveOrderDate);
  cutoffDate.setHours(CUTOFF_HOUR, 0, 0, 0);

  return { orderByLabel, pickupDay, shipDay, cutoffDate };
}

// =============================================================================
// DOM UPDATE FUNCTIONS
// =============================================================================

/**
 * Format milliseconds as HH:MM:SS
 * @param {number} milliseconds
 * @returns {string}
 */
function formatCountdown(milliseconds) {
  if (milliseconds <= 0) {
    return '00:00:00';
  }

  const totalSeconds = Math.floor(milliseconds / 1000);
  const hours = String(Math.floor(totalSeconds / 3600)).padStart(2, '0');
  const minutes = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, '0');
  const seconds = String(totalSeconds % 60).padStart(2, '0');

  return `${hours}:${minutes}:${seconds}`;
}

/**
 * Update the hero cutoffs section
 * @param {string[]} holidays
 * @param {string[]} customClosures
 */
function updateCutoffs(holidays, customClosures) {
  const { orderByLabel, pickupDay, shipDay } = calculateCutoffDates(holidays, customClosures);

  const orderByEl = document.getElementById('orderby-day');
  const pickupEl = document.getElementById('pickup-day');
  const shipEl = document.getElementById('ship-day');

  if (orderByEl) orderByEl.textContent = orderByLabel;
  if (pickupEl) pickupEl.textContent = pickupDay;
  if (shipEl) shipEl.textContent = shipDay;
}

/**
 * Update the PDP countdown timer
 * @param {string[]} holidays
 * @param {string[]} customClosures
 */
function updateTimer(holidays, customClosures) {
  const timerElement = document.getElementById('cutoff-timer');
  const timerDayElement = document.getElementById('cutoff-timer-day');

  if (!timerElement) return;

  const { pickupDay, cutoffDate } = calculateCutoffDates(holidays, customClosures);
  const now = getCentralTime();

  const timeRemaining = cutoffDate.getTime() - now.getTime();
  const formattedTime = formatCountdown(timeRemaining);

  if (timerElement.textContent !== formattedTime) {
    timerElement.textContent = formattedTime;
  }

  if (timerDayElement && timerDayElement.textContent !== pickupDay) {
    timerDayElement.textContent = pickupDay;
  }
}

// =============================================================================
// INITIALIZATION
// =============================================================================

/**
 * Initialize cutoffs with holiday data
 */
async function initCutoffs() {
  // Load holidays (from cache or API)
  const holidays = await loadHolidays();
  const customClosures = getCustomClosures();

  // Update hero cutoffs if present
  if (document.getElementById('cutoffs')) {
    updateCutoffs(holidays, customClosures);
    setInterval(() => updateCutoffs(holidays, customClosures), 30000);
  }

  // Update PDP timer if present
  if (document.getElementById('cutoff-timer')) {
    updateTimer(holidays, customClosures);
    setInterval(() => updateTimer(holidays, customClosures), 1000);
  }
}

// Start initialization
initCutoffs();
